import { useState, useRef } from "react";
import { Quote, Star, Share2, Maximize2, Play, Images } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import Lightbox from "@/components/site/Lightbox";
import { detectVideoKind, youtubeEmbedUrl } from "@/lib/media";
import { projectPhotos, coverSrc, flattenSlides, firstSlideIndex } from "@/lib/gallery";

const SPANS = [
  "col-span-full md:col-span-8 row-span-2 min-h-[300px] md:min-h-[520px]",
  "col-span-full md:col-span-4 row-span-1 min-h-[250px]",
  "col-span-full md:col-span-4 row-span-2 min-h-[300px] md:min-h-[520px]",
  "col-span-full md:col-span-8 row-span-1 min-h-[250px]",
];

async function shareProject(item) {
  if (!item?.slug) return;
  const url = `${window.location.origin}/project/${item.slug}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: `LC Builders — ${item.label}`, url });
      return;
    } catch {
      /* cancelled */
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  } catch {
    toast.error("Unable to copy — " + url);
  }
}

export default function Gallery() {
  const { content } = useContent();
  const allTiles = content?.gallery || [];
  const sections = content?.sections || {};
  const galleryCopy = sections.gallery_subtitle ||
    "A look at the kind of work we're known for around Central Texas — from metal buildings to hardworking fences. Click any project to see it up close.";
  const featured = allTiles.find((t) => t.featured);
  const tiles = allTiles.filter((t) => !t.featured);
  const slides = flattenSlides(allTiles);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const openProject = (projectId) => {
    const idx = firstSlideIndex(slides, projectId);
    if (idx >= 0) setLightboxIdx(idx);
  };

  if (!allTiles.length) return null;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">SELECT WORK</div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-none tracking-tight">
              The Portfolio<br />
              <span className="text-[#9CA3AF]">Our Work Speaks Loud</span>
            </h2>
          </div>
          <p data-testid="gallery-subtitle" className="text-[#374151] max-w-md">
            {galleryCopy}
          </p>
        </div>

        {featured && <FeaturedProject item={featured} onOpen={() => openProject(featured.id)} />}

        {tiles.length > 0 && (
          <div data-testid="gallery-grid" className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 md:gap-6">
            {tiles.map((t, i) => (
              <GalleryTile
                key={t.id || i}
                item={t}
                index={i}
                span={SPANS[i % SPANS.length]}
                onOpen={() => openProject(t.id)}
              />
            ))}
          </div>
        )}
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          items={slides}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndexChange={setLightboxIdx}
        />
      )}
    </section>
  );
}

function GalleryTile({ item, index, span, onOpen }) {
  const videoKind = detectVideoKind(item.video_url);
  const img = coverSrc(item);
  const photoCount = projectPhotos(item).length;
  const videoRef = useRef();

  const handleMouseEnter = () => {
    if (videoKind === "mp4" && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };
  const handleMouseLeave = () => {
    if (videoKind === "mp4" && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`gallery-tile-${index}`}
      onClick={onOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`relative overflow-hidden group border border-[#E5E7EB] text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#84FB00] ${span}`}
    >
      {videoKind === "mp4" ? (
        <video
          ref={videoRef}
          src={item.video_url}
          poster={img}
          muted
          loop
          playsInline
          preload="metadata"
          data-testid={`gallery-tile-${index}-video`}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : videoKind === "youtube" ? (
        // YouTube tile shows the poster + play icon; the video plays only in the lightbox
        img ? (
          <img src={img} alt={item.alt || item.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="absolute inset-0 bg-white" />
        )
      ) : img ? (
        <img
          src={img}
          alt={item.alt || item.label}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      ) : (
        <div className="absolute inset-0 bg-white flex items-center justify-center text-[#9CA3AF] text-sm">No image</div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-transparent to-transparent" />

      {/* Hover icon: expand for images, play for videos */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="w-14 h-14 bg-[#84FB00] flex items-center justify-center">
          {videoKind ? <Play size={22} className="text-[#0A0E1A] fill-white" /> : <Maximize2 size={22} className="text-[#0A0E1A]" />}
        </div>
      </div>

      {/* Persistent badges (top-right) */}
      {videoKind ? (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#84FB00] px-2 py-1 text-[10px] font-display tracking-widest uppercase text-[#0A0E1A]">
          <Play size={10} className="fill-white" /> Video
        </div>
      ) : photoCount > 1 ? (
        <div
          data-testid={`gallery-tile-${index}-photo-count`}
          className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/90 border border-[#D1D5DB] px-2 py-1 text-[10px] font-display tracking-widest uppercase text-[#0A0E1A]"
        >
          <Images size={10} /> {photoCount} Photos
        </div>
      ) : null}

      <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
        <div>
          <div className="text-xs tracking-[0.3em] text-[#4AB300] font-display">
            {String(index + 1).padStart(2, "0")}
          </div>
          <div className="font-display uppercase text-2xl md:text-3xl tracking-wide text-[#0A0E1A] mt-1">
            {item.label}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            shareProject(item);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          data-testid={`gallery-share-${index}`}
          aria-label={`Share ${item.label}`}
          className="inline-flex items-center gap-1 border border-[#D1D5DB] hover:border-[#84FB00] hover:text-[#4AB300] text-[#1F2937] px-3 py-2 text-[10px] font-display tracking-widest uppercase transition-colors bg-white/40"
        >
          <Share2 size={12} /> Share
        </button>
      </div>
    </div>
  );
}

function FeaturedProject({ item, onOpen }) {
  const videoKind = detectVideoKind(item.video_url);
  const img = coverSrc(item);
  const photoCount = projectPhotos(item).length;

  return (
    <div
      data-testid="featured-project"
      className="relative overflow-hidden border-2 border-[#84FB00] mb-10 md:mb-14 grid md:grid-cols-2 min-h-[420px] md:min-h-[520px] bg-white"
    >
      <div className="relative min-h-[280px] md:min-h-[520px]">
        {videoKind === "mp4" ? (
          <video
            src={item.video_url}
            poster={img}
            autoPlay
            muted
            loop
            playsInline
            data-testid="featured-project-video"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : videoKind === "youtube" ? (
          <iframe
            title={item.label}
            src={youtubeEmbedUrl(item.video_url, { background: true })}
            data-testid="featured-project-iframe"
            allow="autoplay; encrypted-media"
            className="absolute inset-0 w-full h-full border-0 pointer-events-none"
          />
        ) : img ? (
          <img src={img} alt={item.alt || item.label} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[#EAEDF2]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-transparent md:from-transparent pointer-events-none" />
        <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-[#84FB00] px-4 py-2">
          <Star size={14} className="fill-white text-[#0A0E1A]" />
          <span className="font-display tracking-[0.25em] uppercase text-xs text-[#0A0E1A]">Featured Project</span>
        </div>
      </div>

      <div className="relative p-8 md:p-12 flex flex-col justify-center">
        <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">PROJECT OF THE MONTH</div>
        <h3 data-testid="featured-project-label" className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#0A0E1A]">
          {item.label}
        </h3>

        {item.featured_quote && (
          <div className="mt-8 border-l-2 border-[#84FB00] pl-5">
            <Quote size={28} className="text-[#4AB300]/40 mb-3" />
            <p className="text-[#1F2937] text-lg leading-relaxed italic">"{item.featured_quote}"</p>
            {item.featured_author && (
              <p className="mt-4 font-display tracking-widest uppercase text-sm text-[#4B5563]">— {item.featured_author}</p>
            )}
          </div>
        )}

        {!item.featured_quote && item.alt && (
          <p className="mt-6 text-[#374151] leading-relaxed">{item.alt}</p>
        )}

        {item.slug && (
          <div className="mt-8 flex flex-wrap gap-3">
            {photoCount > 1 && onOpen && (
              <button
                type="button"
                onClick={onOpen}
                data-testid="featured-project-view-photos"
                className="inline-flex items-center gap-2 bg-[#84FB00] hover:bg-[#6EE500] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm"
              >
                <Images size={14} /> See All {photoCount} Photos
              </button>
            )}
            <a
              href={`/project/${item.slug}`}
              data-testid="featured-project-view"
              className={`inline-flex items-center gap-2 px-5 py-3 font-display tracking-widest uppercase text-sm ${
                photoCount > 1 && onOpen
                  ? "border border-[#D1D5DB] hover:border-[#84FB00] hover:text-[#4AB300] text-[#0A0E1A] transition-colors"
                  : "bg-[#84FB00] hover:bg-[#6EE500] text-[#0A0E1A]"
              }`}
            >
              View Project
            </a>
            <button
              onClick={() => shareProject(item)}
              data-testid="featured-project-share"
              className="inline-flex items-center gap-2 border border-[#D1D5DB] hover:border-[#84FB00] hover:text-[#4AB300] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm transition-colors"
            >
              <Share2 size={14} /> Share
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
